import { Injectable } from '@angular/core';
import { takeUntil } from 'rxjs';
import { UserGender } from 'src/app/core/common';
import { WhiteLabelChartStatistics } from 'src/app/core/data/model';
import { WhiteLabelDashboardRepository } from 'src/app/core/data/repository';
import { Bloc, ErrorState, InitEvent, ShowLoadingComponentState } from 'src/app/shared/bloc/base-bloc';
import { LabelMapForMaleAndFemalesValue, LabelMapForMalesAndFemales } from 'src/app/shared/common';
import { WhiteLabelDashboardLabelsAndDataSets } from 'src/app/shared/interfaces';
import { NonDrawing } from 'src/app/shared/model';

import { StatisticsChartEvent } from './statistics-chart-event';
import { StatisticsChartReadyStateState, StatisticsChartState } from './statistics-chart-state';

@Injectable()
export class StatisticsChartBloc extends Bloc<StatisticsChartEvent, StatisticsChartState> {
  constructor(private whiteLabelDashboardRepository: WhiteLabelDashboardRepository) {
    super();
  }
  onIncomingEvents(event: StatisticsChartEvent): void {
    if (event instanceof InitEvent) {
      // loading component
      this.onState$.next(new ShowLoadingComponentState(true));
      // call api
      this.whiteLabelDashboardRepository
        .getChartStatistics(event.args)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            const { male, female, labels } = this.prepareChartData(result);
            this.onState$.next(new StatisticsChartReadyStateState(male, female, labels));
          },
          error: (error) => {
            this.onState$.next(new ErrorState<NonDrawing>(new NonDrawing(), error));
          },
        });
    }
  }

  private prepareChartData(chartData: WhiteLabelChartStatistics[]): WhiteLabelDashboardLabelsAndDataSets {
    const orderedChartListByLabelAccumulator: LabelMapForMalesAndFemales<number> = new Map();
    const orderedChartListByLabel = chartData.reduce((object, item) => {
      if (object.has(item.label)) {
        if (item.gender == UserGender.MALE) {
          object.set(item.label, {
            male: [...object.get(item.label).male, item.count],
            female: [...object.get(item.label).female],
          });
        }
        if (item.gender == UserGender.FEMALE) {
          object.set(item.label, {
            male: [...object.get(item.label).male],
            female: [...object.get(item.label).female, item.count],
          });
        }
      } else {
        if (item.gender == UserGender.MALE) {
          object.set(item.label, { male: [item.count], female: [] });
        }
        if (item.gender == UserGender.FEMALE) {
          object.set(item.label, { male: [], female: [item.count] });
        }
      }
      return object;
    }, orderedChartListByLabelAccumulator);

    // convert map values to array
    const orderedChartListByLabelArray: LabelMapForMaleAndFemalesValue<number>[] = [
      ...orderedChartListByLabel.values(),
    ];

    const orderedChartListByLabelArrayAfterEqualMaleWithFemales = orderedChartListByLabelArray.reduce((obj, item) => {
      if (item.male.length > item.female.length) {
        item.female.push(...[...Array(item.male.length - item.female.length).fill(0)]);
      }
      if (item.female.length > item.male.length) {
        item.male.push(...[...Array(item.female.length - item.male.length).fill(0)]);
      }
      return obj;
    }, orderedChartListByLabelArray);
    const dataSetsAndLabels: WhiteLabelDashboardLabelsAndDataSets = {
      male: orderedChartListByLabelArrayAfterEqualMaleWithFemales.flatMap((item) => item.male),
      female: orderedChartListByLabelArrayAfterEqualMaleWithFemales.flatMap((item) => item.female),
      labels: [...orderedChartListByLabel.keys()].sort((prev, next) => +prev - +next),
    };
    return dataSetsAndLabels;
  }
}
